import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  connectUappi,
  disconnectUappi,
  getUappiIntegration,
  syncUappiContacts,
  TenantIntegration,
  testUappiConnection,
  updateUappi,
} from "../../../helpers/integrations_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../helpers/tenantTimezone";

type FormValues = {
  store_base_url: string;
  api_token: string;
};

const UAPPI_HEADER_NAME = "X-Brenvio-Uappi-Secret";

const ABANDON_JSON_SAMPLE = `{
  "email": "cliente@exemplo.com",
  "nome": "Maria",
  "cart_url": "https://minhaloja.com.br/carrinho/abc",
  "valor": "120.00",
  "id": "evt-1"
}`;

function checklistKey(publicKey: string, part: string): string {
  return `brenvio:uappi-checklist:${publicKey}:${part}`;
}

/**
 * Conectar / gerenciar Uappi — token API + checklist (DOC 11 § 6.3).
 * Por quê: header + JSON de abandono não são óbvios no painel Uappi.
 */
const UappiIntegrationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language || "pt-BR";
  const tz = readDisplayTimezone();

  document.title = t("integrations.uappi.documentTitle");
  const helpHref = helpUrlForRoute("/integrations/uappi", locale);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [row, setRow] = useState<TenantIntegration | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testOk, setTestOk] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [headerCopied, setHeaderCopied] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUappiIntegration();
      if (res?.success) {
        setRow(res.data);
        const pk = res.data.public_key;
        setUrlCopied(localStorage.getItem(checklistKey(pk, "url")) === "1");
        setHeaderCopied(localStorage.getItem(checklistKey(pk, "header")) === "1");
        setJsonCopied(localStorage.getItem(checklistKey(pk, "json")) === "1");
        setTestOk(localStorage.getItem(checklistKey(pk, "test")) === "1");
      } else {
        setRow(null);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setRow(null);
      } else {
        setError(apiErrorMessage(e, t("integrations.error.load")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const markCheck = (part: string) => {
    if (!row?.public_key) return;
    localStorage.setItem(checklistKey(row.public_key, part), "1");
    if (part === "url") setUrlCopied(true);
    if (part === "header") setHeaderCopied(true);
    if (part === "json") setJsonCopied(true);
    if (part === "test") setTestOk(true);
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      store_base_url: row?.account_name ? `https://${row.account_name}` : "",
      api_token: "",
    },
    validationSchema: Yup.object({
      store_base_url: Yup.string().required(t("integrations.uappi.urlRequired")),
      api_token: Yup.string().when([], {
        is: () => !row,
        then: (s) => s.required(t("integrations.uappi.tokenRequired")),
        otherwise: (s) => s,
      }),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        if (!row) {
          const res = await connectUappi(values);
          if (res?.success) {
            setRow(res.data);
            if (res.data.webhook_secret) {
              setRevealedSecret(res.data.webhook_secret);
            }
            toast.success(t("integrations.uappi.toastConnected"));
          }
        } else {
          const payload: Record<string, string | boolean> = {
            store_base_url: values.store_base_url,
          };
          if (values.api_token) payload.api_token = values.api_token;
          const res = await updateUappi(payload);
          if (res?.success) {
            setRow(res.data);
            formik.setFieldValue("api_token", "");
            toast.success(t("integrations.uappi.toastUpdated"));
          }
        }
      } catch (e) {
        toast.error(apiErrorMessage(e, t("integrations.error.save")));
      } finally {
        setSaving(false);
      }
    },
  });

  const onTest = async () => {
    setTesting(true);
    try {
      const res = await testUappiConnection();
      if (res?.success) {
        markCheck("test");
        toast.success(res.data?.message || t("integrations.uappi.toastTestOk"));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.test")));
    } finally {
      setTesting(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      await syncUappiContacts();
      toast.success(t("integrations.uappi.toastSyncQueued"));
      setTimeout(() => void load(), 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.sync")));
    } finally {
      setSyncing(false);
    }
  };

  const onRotate = async () => {
    try {
      const res = await updateUappi({ rotate_webhook_secret: true });
      if (res?.success && res.data.webhook_secret) {
        setRevealedSecret(res.data.webhook_secret);
        setRow(res.data);
        if (res.data.public_key) {
          localStorage.removeItem(checklistKey(res.data.public_key, "header"));
          setHeaderCopied(false);
        }
        toast.success(t("integrations.uappi.toastRotated"));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.save")));
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm(t("integrations.uappi.disconnectConfirm"))) return;
    try {
      await disconnectUappi();
      toast.success(t("integrations.uappi.toastDisconnected"));
      navigate("/integrations");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.disconnect")));
    }
  };

  const copy = async (text: string, okKey: string, checkPart?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (checkPart) markCheck(checkPart);
      toast.success(t(okKey));
    } catch {
      toast.error(t("integrations.error.copy"));
    }
  };

  const checklistItems = row
    ? [
        { id: "creds", done: true, label: t("integrations.uappi.check.creds") },
        { id: "test", done: testOk, label: t("integrations.uappi.check.test") },
        { id: "url", done: urlCopied, label: t("integrations.uappi.check.url") },
        {
          id: "header",
          done: headerCopied,
          label: t("integrations.uappi.check.header"),
        },
        {
          id: "json",
          done: jsonCopied,
          label: t("integrations.uappi.check.json"),
        },
      ]
    : [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("integrations.uappi.title")}
            pageTitle={t("menu.integrations")}
          />
          <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
            <DismissiblePageTip tipId="integrations-uappi">
              {t("integrations.uappi.tip")}
            </DismissiblePageTip>
            <PageHelpLink href={helpHref} label={t("integrations.help")} />
          </div>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={7}>
                {!row && (
                  <Card className="mb-3">
                    <CardHeader>
                      <h5 className="card-title mb-0">
                        {t("integrations.uappi.wizardTitle")}
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <ListGroup flush>
                        <ListGroupItem>{t("integrations.uappi.wizard.step1")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.uappi.wizard.step2")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.uappi.wizard.step3")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.uappi.wizard.step4")}</ListGroupItem>
                      </ListGroup>
                    </CardBody>
                  </Card>
                )}
                <Card>
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      {row
                        ? t("integrations.uappi.manageTitle")
                        : t("integrations.uappi.connectTitle")}
                    </h5>
                    {row && (
                      <Badge color={row.enabled ? "success" : "secondary"}>
                        {row.enabled
                          ? t("integrations.connected")
                          : t("integrations.disabled")}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardBody>
                    <Form onSubmit={formik.handleSubmit}>
                      <div className="mb-3">
                        <Label>{t("integrations.uappi.storeUrl")}</Label>
                        <Input
                          name="store_base_url"
                          value={formik.values.store_base_url}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(
                              formik.touched.store_base_url &&
                              formik.errors.store_base_url
                            )
                          }
                          placeholder="https://minhaloja.com.br"
                        />
                        <FormFeedback>{formik.errors.store_base_url}</FormFeedback>
                      </div>
                      <div className="mb-3">
                        <Label>{t("integrations.uappi.apiToken")}</Label>
                        <Input
                          type="password"
                          name="api_token"
                          autoComplete="off"
                          value={formik.values.api_token}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(formik.touched.api_token && formik.errors.api_token)
                          }
                          placeholder={
                            row?.app_key_masked
                              ? t("integrations.uappi.tokenKeep")
                              : undefined
                          }
                        />
                        <FormFeedback>{formik.errors.api_token}</FormFeedback>
                        <p className="text-muted small mb-0 mt-1">
                          {t("integrations.uappi.tokenHelp")}
                        </p>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button color="primary" type="submit" disabled={saving}>
                          {saving && <Spinner size="sm" className="me-1" />}
                          {row
                            ? t("integrations.uappi.save")
                            : t("integrations.connect")}
                        </Button>
                        {row && (
                          <>
                            <Button
                              color="secondary"
                              outline
                              type="button"
                              disabled={testing}
                              onClick={() => void onTest()}
                            >
                              {testing && <Spinner size="sm" className="me-1" />}
                              {t("integrations.uappi.test")}
                            </Button>
                            <Button
                              color="success"
                              outline
                              type="button"
                              disabled={syncing}
                              onClick={() => void onSync()}
                            >
                              {syncing && <Spinner size="sm" className="me-1" />}
                              {t("integrations.uappi.sync")}
                            </Button>
                            <Button
                              color="danger"
                              outline
                              type="button"
                              onClick={() => void onDisconnect()}
                            >
                              {t("integrations.uappi.disconnect")}
                            </Button>
                          </>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={5}>
                {row && (
                  <>
                    <Card className="mb-3">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("integrations.uappi.checklistTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <p className="text-muted small">
                          {t("integrations.uappi.checklistTip")}
                        </p>
                        <ListGroup flush>
                          {checklistItems.map((item) => (
                            <ListGroupItem
                              key={item.id}
                              className="px-0 d-flex align-items-center gap-2"
                            >
                              <i
                                className={
                                  item.done
                                    ? "ri-checkbox-circle-fill text-success"
                                    : "ri-checkbox-blank-circle-line text-muted"
                                }
                              />
                              <span>{item.label}</span>
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("integrations.uappi.webhookTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <p className="text-muted small">
                          {t("integrations.uappi.webhookHelp")}
                        </p>
                        <Label>{t("integrations.uappi.webhookUrl")}</Label>
                        <div className="d-flex gap-2 mb-3">
                          <Input readOnly value={row.webhook_url} />
                          <Button
                            color="light"
                            onClick={() =>
                              void copy(
                                row.webhook_url,
                                "integrations.uappi.toastCopied",
                                "url"
                              )
                            }
                          >
                            {t("integrations.uappi.copy")}
                          </Button>
                        </div>
                        <Label>{t("integrations.uappi.headerNameLabel")}</Label>
                        <div className="d-flex gap-2 mb-2">
                          <Input readOnly value={UAPPI_HEADER_NAME} />
                          <Button
                            color="light"
                            onClick={() =>
                              void copy(
                                UAPPI_HEADER_NAME,
                                "integrations.uappi.toastCopied"
                              )
                            }
                          >
                            {t("integrations.uappi.copy")}
                          </Button>
                        </div>
                        <Label>{t("integrations.uappi.webhookSecret")}</Label>
                        <div className="d-flex gap-2 mb-2">
                          <Input
                            readOnly
                            value={
                              revealedSecret ||
                              t("integrations.uappi.secretHidden")
                            }
                          />
                          {revealedSecret ? (
                            <Button
                              color="primary"
                              outline
                              onClick={() =>
                                void copy(
                                  revealedSecret,
                                  "integrations.uappi.toastHeaderCopied",
                                  "header"
                                )
                              }
                            >
                              {t("integrations.uappi.copySecret")}
                            </Button>
                          ) : (
                            <Button color="light" onClick={() => void onRotate()}>
                              {t("integrations.uappi.rotate")}
                            </Button>
                          )}
                        </div>
                        {revealedSecret && (
                          <Button
                            color="light"
                            size="sm"
                            className="mb-2"
                            onClick={() =>
                              void copy(
                                `${UAPPI_HEADER_NAME}: ${revealedSecret}`,
                                "integrations.uappi.toastHeaderCopied",
                                "header"
                              )
                            }
                          >
                            {t("integrations.uappi.copyHeaderLine")}
                          </Button>
                        )}
                        <p className="small text-muted mb-0">
                          {t("integrations.uappi.headerNote")}
                        </p>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("integrations.uappi.abandonJsonTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <p className="text-muted small">
                          {t("integrations.uappi.abandonJsonHelp")}
                        </p>
                        <pre className="bg-light border rounded p-2 small mb-2">
                          {ABANDON_JSON_SAMPLE}
                        </pre>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() =>
                            void copy(
                              ABANDON_JSON_SAMPLE,
                              "integrations.uappi.toastCopied",
                              "json"
                            )
                          }
                        >
                          {t("integrations.uappi.copyJson")}
                        </Button>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardBody>
                        <p className="mb-1">
                          <strong>{t("integrations.uappi.customersSynced")}:</strong>{" "}
                          {row.customer_count ?? 0}
                        </p>
                        <p className="mb-1 small text-muted">
                          {t("integrations.uappi.lastSync")}:{" "}
                          {row.last_synced_at
                            ? formatInTenantTimezone(row.last_synced_at, tz, locale)
                            : "—"}
                        </p>
                        {!row.automation_enabled && (
                          <Alert color="warning" className="mb-0 mt-2">
                            {t("integrations.uappi.automationOff")}{" "}
                            <Link to="/automations/abandoned-cart">
                              {t("integrations.uappi.openAutomation")}
                            </Link>
                          </Alert>
                        )}
                      </CardBody>
                    </Card>
                  </>
                )}
                <Button color="link" tag={Link} to="/integrations" className="px-0">
                  {t("integrations.backToHub")}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default UappiIntegrationPage;
